import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  MessageSquare,
  ShoppingCart,
  Calendar,
  Clock,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Mail,
  // CreditCard,
  ExternalLink,
  Weight,
  Package,
  AlertTriangle,
  // X,
  CheckCircle,
  Send,
  XCircle,
  Play,
  Pause
} from 'lucide-react';

interface Message {
  id: string;
  content: string;
  direction: 'incoming' | 'outgoing';
  created_at: string;
}

interface ShoppingListItem {
  id: string;
  product_name: string;
  product_price: number;
  quantity: number;
  product_image?: string;
}

interface ShoppingList {
  id: string;
  name: string;
  status: string;
  created_at: string;
  shopping_list_items: ShoppingListItem[];
  woocommerce_order_id?: string;
  applied_subscription_id?: string;
}

interface UserDetail {
  id: string;
  name: string;
  phone: string;
  email?: string;
  created_at: string;
  last_activity: string;
  botPaused?: boolean;
  bot_enabled?: boolean;
  atendimento?: boolean;
  categoria?: string;
  origem?: string;
  descricao_inicial?: string;
  seller?: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
}

interface UserData {
  user: UserDetail;
  messages: Message[];
  shoppingLists: ShoppingList[];
}

interface PaymentLinkResponse {
  payment_link: string;
  amount_brl: number;
  order_summary: string;
}

interface Subscription {
  id: string;
  name: string;
  price_monthly: number;
  max_weight_kg: number;
  shipping_discount: number;
  handling_discount: number;
  benefits: string[];
  limitations: string[];
  active: boolean;
}

const UsuarioDetalhe = () => {
  const { id } = useParams();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'messages' | 'lists'>('messages');
  const [expandedLists, setExpandedLists] = useState<string[]>([]);
  const [creatingPaymentLink, setCreatingPaymentLink] = useState<string | null>(null);
  const [customAmount, setCustomAmount] = useState<number | null>(null);
  const [showCustomAmount, setShowCustomAmount] = useState<string | null>(null);
  // const [applyingSubscription, setApplyingSubscription] = useState<string | null>(null);
  // const [removingSubscription, setRemovingSubscription] = useState<string | null>(null);
  // const [selectedSubscription, setSelectedSubscription] = useState<string>('');
  const [applyTaxesOnPayment, setApplyTaxesOnPayment] = useState<boolean>(true);
  const [newMessage, setNewMessage] = useState('');
  const [botPaused, setBotPaused] = useState(false);
  const [botLoading, setBotLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sendOnEnter, setSendOnEnter] = useState(true);
  const [aiValidating, setAiValidating] = useState(false);
  const [aiValidationResult, setAiValidationResult] = useState<{show: boolean, approved: boolean, message: string} | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Recuperar usuário logado
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isTrainee = currentUser?.role === 'trainee';

  useEffect(() => {
    if (id) {
      loadUserData(id);
      loadSubscriptions();
      loadConfigurations();
    }
  }, [id]);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!messagesContainerRef.current || !messagesEndRef.current) return;
  
    const container = messagesContainerRef.current;
    // Checa se o usuário está perto do fim (por exemplo, menos de 100px do fim)
    const isAtBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < 100;
  
    if (isAtBottom) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [userData?.messages]);

  // Polling para buscar novas mensagens automaticamente
  useEffect(() => {
    if (!id || activeTab !== 'messages') return;
    const interval = setInterval(() => {
      loadUserData(id);
    }, 3000);
    return () => clearInterval(interval);
  }, [id, activeTab]);

  const loadUserData = async (userId: string) => {
    try {
      const response = await fetch(`/api/users/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUserData(data.data);
        setBotPaused(data.data.user.botPaused || false);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptions = async () => {
    try {
      const response = await fetch('/api/subscriptions', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data.data?.filter((sub: Subscription) => sub.active) || []);
      }
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    }
  };

  const loadConfigurations = async () => {
    try {
      const response = await fetch('/api/configurations', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setApplyTaxesOnPayment(data.data?.apply_taxes_on_payment !== false);
      }
    } catch (error) {
      console.error('Error loading configurations:', error);
    }
  };

  const handleSendMessage = async () => {
    if (sendingMessage || aiValidating) return; // evita duplo clique
    
    if (!newMessage.trim() || !userData) return;

    // Validar mensagem com IA antes de enviar
    await validateMessageWithAI();
  };

  const validateMessageWithAI = async () => {
    if (!userData) return;

    setAiValidating(true);
    setAiValidationResult(null);

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;

      // Validar mensagem com IA
      const response = await fetch(`${apiUrl}/api/ai/validate-message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: newMessage
        })
      });

      const data = await response.json();

      if (data.success) {
        const validation = data.data;

        if (validation.valid) {
          // Mensagem aprovada - enviar
          setAiValidationResult({
            show: true,
            approved: true,
            message: 'Mensagem aprovada pela IA'
          });

          // Usar mensagem corrigida se houver
          const messageToSend = validation.corrected_message || newMessage;
          
          setTimeout(() => {
            sendMessageToUser(messageToSend);
            setAiValidationResult(null);
          }, 1000);
        } else {
          // Mensagem rejeitada
          const issues = validation.issues.join(', ');
          const suggestions = validation.suggestions.length > 0 
            ? '\n\nSugestões: ' + validation.suggestions.join(', ')
            : '';
          
          setAiValidationResult({
            show: true,
            approved: false,
            message: `Mensagem bloqueada\n\nProblemas: ${issues}${suggestions}${validation.corrected_message ? '\n\nMensagem sugerida:\n' + validation.corrected_message : ''}`
          });

          // Se houver mensagem corrigida, substituir automaticamente
          if (validation.corrected_message && validation.severity !== 'high') {
            setTimeout(() => {
              setNewMessage(validation.corrected_message);
              setAiValidationResult(null);
            }, 3000);
          } else if (validation.severity === 'high') {
            // Severidade alta: notificar supervisor se for trainee
            if (isTrainee) {
              await notifySupervisor(issues);
            }
            // Limpar após 5 segundos
            setTimeout(() => {
              setAiValidationResult(null);
            }, 5000);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao validar com IA:', error);
      // Em caso de erro, permitir envio (fallback)
      sendMessageToUser(newMessage);
    } finally {
      setAiValidating(false);
    }
  };

  const notifySupervisor = async (feedback: string) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
      
      await fetch(`${apiUrl}/api/chat/notify-supervisor`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          trainee_id: currentUser.id,
          trainee_name: currentUser.name,
          customer_phone: userData?.user.phone,
          customer_name: userData?.user.name,
          blocked_message: newMessage,
          ai_feedback: feedback
        })
      });
    } catch (error) {
      console.error('Erro ao notificar supervisor:', error);
    }
  };

  const sendMessageToUser = async (msg: string) => {
    setSendingMessage(true);
    try {
      if (!msg.trim() || !userData) { setSendingMessage(false); return; }
      setNewMessage('');
      setAiValidationResult(null);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }

      // Atualiza UI imediatamente
      const message: Message = {
        id: Date.now().toString(),
        content: msg,
        direction: 'outgoing',
        created_at: new Date().toISOString()
      };
      setUserData({
        ...userData,
        messages: [...userData.messages, message]
      });

      // Dispara para Evolution/Instagram
      try {
        const response = await fetch('/api/setText', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            userId: userData.user.id,
            phone: userData.user.phone,
            msg: msg
          })
        });

        const result = await response.json();
        
        // Verificar se houve erro de janela do Instagram
        if (!result.success && (result.error === 'instagram_window_expired' || result.error === 'instagram_no_incoming_message')) {
          alert(result.message);
          // Remover mensagem temporária da UI
          setUserData({
            ...userData,
            messages: userData.messages.filter(m => m.id !== Date.now().toString())
          });
          setSendingMessage(false);
          return;
        }
      } catch (err) {
        console.error('Erro ao enviar mensagem:', err);
      }

      // Salva no histórico (banco)
      try {
        await fetch('/api/send-admin-message', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            user_id: userData.user.id,
            content: msg
          })
        });
      } catch (err) {
        console.error('Erro ao registrar mensagem manual no histórico:', err);
      }

      // Marca bot_enabled como false
      try {
        await fetch(`/api/users/${userData.user.id}/toggle-bot`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ enabled: false, atendimento: true })
        });
        setUserData(prev => prev ? ({
          ...prev,
          user: {
            ...prev.user,
            bot_enabled: false,
            atendimento: true
          }
        }) : prev);
      } catch (err) {
        console.error('Erro ao marcar bot_enabled como false:', err);
      }

      // Atribuir atendente automaticamente ao enviar mensagem
      try {
        const token = localStorage.getItem('token');
        const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
        
        await fetch(`${apiUrl}/api/chats/assign`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            user_id: userData.user.id,
            seller_id: currentUser.id
          })
        });
        
        console.log(`✅ Atendente ${currentUser.name} atribuído ao cliente ${userData.user.name}`);
      } catch (err) {
        console.error('Erro ao atribuir atendente:', err);
      }

      loadUserData(id!);
    } catch (err) {
      console.error('Erro ao enviar mensagem:', err);
    } finally {
      setSendingMessage(false);
    }
  };

  const toggleBotPause = async () => {
    if (!userData) return;

    setBotLoading(true);
    try {
      const newPausedState = !botPaused;
      setBotPaused(newPausedState);
      // Atualiza o status do bot_enabled localmente para refletir a mudança na tag
      setUserData(prev => prev ? ({
        ...prev,
        user: {
          ...prev.user,
          bot_enabled: !prev.user.bot_enabled,
          atendimento: !prev.user.bot_enabled ? false : true
        }
      }) : prev);
      // Update user's bot enabled state
      const response = await fetch(`/api/users/${id}/toggle-bot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          enabled: !userData.user.bot_enabled,
          atendimento: !userData.user.bot_enabled ? false : true
        })
      });

      if (!response.ok) {
        console.error('Error toggling bot state:', await response.text());
        // Revert state if API call fails
        setBotPaused(!newPausedState);
        setUserData(prev => prev ? ({
          ...prev,
          user: {
            ...prev.user,
            bot_enabled: !prev.user.bot_enabled,
            atendimento: !prev.user.bot_enabled ? false : true
          }
        }) : prev);
      }
    } catch (error) {
      console.error('Error toggling bot state:', error);
      // Revert state if API call fails
      setBotPaused(!botPaused);
      setUserData(prev => prev ? ({
        ...prev,
        user: {
          ...prev.user,
          bot_enabled: !prev.user.bot_enabled
        }
      }) : prev);
    } finally {
      setBotLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const calculateListTotal = (items: ShoppingListItem[]) => {
    return items.reduce((total, item) => total + (item.product_price * item.quantity), 0);
  };

  const calculateTotalWeight = (items: ShoppingListItem[]) => {
    // Assuming 0.5kg per item as default weight
    return items.reduce((total, item) => total + (0.5 * item.quantity), 0);
  };

  const calculateShippingFee = (items: ShoppingListItem[], subscription?: Subscription) => {
    // Simple estimation - in a real app this would use the shipping intervals
    const totalWeight = calculateTotalWeight(items);
    const baseFee = totalWeight * 5; // $5 per kg

    if (subscription) {
      return subscription.shipping_discount;
    }

    return baseFee;
  };

  const calculateHandlingFee = (items: ShoppingListItem[], subscription?: Subscription) => {
    // Calculate handling fee based on weight intervals
    const totalWeight = calculateTotalWeight(items);

    // Example handling fee intervals by weight
    const handlingIntervals = [
      { min: 0, max: 2, rate: 10 },
      { min: 2, max: 5, rate: 15 },
      { min: 5, max: 10, rate: 20 },
      { min: 10, max: 20, rate: 25 },
      { min: 20, max: 30, rate: 30 },
      { min: 30, max: 40, rate: 35 },
      { min: 40, max: 50, rate: 40 },
      { min: 50, max: 100, rate: 50 }
    ];

    // Find the appropriate interval
    let baseFee = 10; // Default minimum
    for (const interval of handlingIntervals) {
      if (totalWeight > interval.min && totalWeight <= interval.max) {
        baseFee = interval.rate;
        break;
      }
    }

    if (subscription) {
      return subscription.handling_discount;
    }

    return baseFee;
  };

  const calculateTaxes = (items: ShoppingListItem[]) => {
    const subtotal = calculateListTotal(items);
    return subtotal * 0.085; // 8.5% tax rate
  };

  const calculateServiceFee = (items: ShoppingListItem[]) => {
    const subtotal = calculateListTotal(items);
    return subtotal * 0.035; // 3.5% service fee
  };

  const calculateBrazilianTaxes = (items: ShoppingListItem[], shipping: number, handlingFees: number) => {
    const subtotal = calculateListTotal(items);
    return (subtotal + shipping + handlingFees) * 0.8; // 80% of product + shipping + handling
  };

  const calculateTotalBRL = (total: number) => {
    return total * 5.9; // Convert to BRL
  };

  const calculateTotalUSD = (items: ShoppingListItem[], subscription?: Subscription) => {
    const subtotal = calculateListTotal(items);
    const serviceFee = calculateServiceFee(items);
    const taxes = calculateTaxes(items);
    const shipping = calculateShippingFee(items, subscription);
    const handling = calculateHandlingFee(items, subscription);

    // Apply taxes based on configuration
    const finalTaxes = applyTaxesOnPayment ? taxes : 0;

    // Calculate Brazilian taxes
    const brazilianTaxes = calculateBrazilianTaxes(items, shipping, handling);

    // Only include Brazilian taxes in the total if we're applying taxes
    const finalBrazilianTaxes = applyTaxesOnPayment ? brazilianTaxes : 0;

    return subtotal + serviceFee + finalTaxes + shipping + handling + finalBrazilianTaxes;
  };

  const getAppliedSubscription = (list: ShoppingList): Subscription | undefined => {
    if (!list.applied_subscription_id) return undefined;
    return subscriptions.find(sub => sub.id === list.applied_subscription_id);
  };

  // const canApplySubscription = (list: ShoppingList, subscription: Subscription) => {
  //   const totalWeight = calculateTotalWeight(list.shopping_list_items || []);
  //   return totalWeight <= subscription.max_weight_kg;
  // };

  // const completeOrder = async (listId: string) => {
  //   try {
  //     setLoading(true);
  //     const response = await fetch(`/api/shopping-lists/${listId}/complete`, {
  //       method: 'POST',
  //       headers: {
  //         Authorization: `Bearer ${localStorage.getItem('token')}`
  //       }
  //     });

  //     if (response.ok) {
  //       const result = await response.json();
  //       console.log('Order completed:', result);
  //       alert('Pedido criado com sucesso no WooCommerce!');
  //       loadUserData(id!);
  //     } else {
  //       const error = await response.json();
  //       alert(`Erro ao completar pedido: ${error.error || 'Erro desconhecido'}`);
  //     }
  //   } catch (error) {
  //     console.error('Error completing order:', error);
  //     alert('Erro ao completar pedido');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const openPaymentLinkForm = (listId: string, items: any[]) => {
    const total = calculateListTotal(items);
    const totalBRL = Math.round(total * 5.83 * 100) / 100; // Conversão USD para BRL com 2 decimais
    setCustomAmount(totalBRL);
    setShowCustomAmount(listId);
  };

  const createPaymentLink = async (listId: string) => {
    try {
      setCreatingPaymentLink(listId);

      const payload: any = {};
      if (showCustomAmount === listId && customAmount) {
        payload.amount_brl = customAmount;
      }

      const response = await fetch(`/api/shopping-lists/${listId}/payment-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        const data: PaymentLinkResponse = result.data;

        // Send payment link to user via message
        await sendPaymentLinkMessage(data.order_summary);

        alert(`Link de pagamento criado com sucesso!\n\nValor: R$${data.amount_brl.toFixed(2)}\nLink: ${data.payment_link}`);

        // Reset states
        setShowCustomAmount(null);
        setCustomAmount(null);

        // Reload user data to show the new message
        loadUserData(id!);
      } else {
        const error = await response.json();
        alert(`Erro ao criar link de pagamento: ${error.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Error creating payment link:', error);
      alert('Erro ao criar link de pagamento');
    } finally {
      setCreatingPaymentLink(null);
    }
  };

  const sendPaymentLinkMessage = async (message: string) => {
    if (!userData) return;

    try {
      const response = await fetch('/api/message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: userData.user.name,
          phone: userData.user.phone,
          email: userData.user.email,
          message: 'Link de pagamento solicitado',
          system_response: message,
          admin_message: true
        })
      });

      if (!response.ok) {
        console.error('Error sending payment link message');
      }
    } catch (error) {
      console.error('Error sending payment link message:', error);
    }
  };

  // const applySubscription = async (listId: string, subscriptionId: string) => {
  //   if (!id) return;

  //   setApplyingSubscription(listId);

  //   try {
  //     const response = await fetch(`/api/shopping-lists/${listId}/apply-subscription`, {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         Authorization: `Bearer ${localStorage.getItem('token')}`
  //       },
  //       body: JSON.stringify({ subscription_id: subscriptionId })
  //     });

  //     const result = await response.json();

  //     if (response.ok) {
  //       alert(`Assinatura aplicada com sucesso!\n\n${result.message}`);
  //       loadUserData(id);
  //     } else {
  //       alert(`Erro ao aplicar assinatura:\n\n${result.error}\n\n${result.details || ''}`);
  //     }
  //   } catch (error) {
  //     console.error('Error applying subscription:', error);
  //     alert('Erro ao aplicar assinatura');
  //   } finally {
  //     setApplyingSubscription(null);
  //     setSelectedSubscription('');
  //   }
  // };

  // const removeSubscription = async (listId: string) => {
  //   if (!id) return;

  //   if (!confirm('Tem certeza que deseja remover a assinatura desta lista?')) {
  //     return;
  //   }

  //   setRemovingSubscription(listId);

  //   try {
  //     const response = await fetch(`/api/shopping-lists/${listId}/remove-subscription`, {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         Authorization: `Bearer ${localStorage.getItem('token')}`
  //       }
  //     });

  //     const result = await response.json();

  //     if (response.ok) {
  //       alert(`Assinatura removida com sucesso!\n\n${result.message}`);
  //       loadUserData(id);
  //     } else {
  //       alert(`Erro ao remover assinatura:\n\n${result.error}`);
  //     }
  //   } catch (error) {
  //     console.error('Error removing subscription:', error);
  //     alert('Erro ao remover assinatura');
  //   } finally {
  //     setRemovingSubscription(null);
  //   }
  // };

  const toggleListExpansion = (listId: string) => {
    setExpandedLists(prev =>
      prev.includes(listId)
        ? prev.filter(id => id !== listId)
        : [...prev, listId]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Usuário não encontrado</h3>
        <Link to="/usuarios" className="mt-4 text-blue-600 hover:text-blue-800">
          Voltar para usuários
        </Link>
      </div>
    );
  }

  const { user, messages, shoppingLists } = userData;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/usuarios"
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
              <span className="text-2xl font-bold text-white">
                {user.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{user.name}</h1>
              <p className="text-sm text-gray-600">{user.phone}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Status Badge */}
            {user.bot_enabled ? (
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold text-pink-700 bg-pink-100 border border-pink-200">
                FORA DE ATENDIMENTO
              </span>
            ) : user.atendimento ? (
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold text-green-700 bg-green-100 border border-green-200">
                EM ATENDIMENTO{userData.user.seller?.name ? ` - ${userData.user.seller.name.toUpperCase()}` : ''}
              </span>
            ) : (
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold text-red-700 bg-red-100 border border-red-200">
                AGUARDANDO
              </span>
            )}
            
            {/* Bot Play/Pause Button */}
            <button
              onClick={toggleBotPause}
              disabled={botLoading}
              className={`group relative inline-flex items-center justify-center w-12 h-12 rounded-lg transition-all ${
                user.bot_enabled 
                  ? 'bg-green-100 hover:bg-green-200 text-green-700' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              } ${botLoading ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`}
              title={user.bot_enabled ? 'Pausar Bot (Atendimento Manual)' : 'Ativar Bot (Atendimento Automático)'}
            >
              {botLoading ? (
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-400 border-t-transparent"></div>
              ) : user.bot_enabled ? (
                <Play className="h-6 w-6" />
              ) : (
                <Pause className="h-6 w-6" />
              )}
              {/* Tooltip */}
              <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                {user.bot_enabled ? 'Pausar Bot' : 'Ativar Bot'}
                <svg className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-2 h-1" viewBox="0 0 8 4">
                  <path fill="#111827" d="M0 0l4 4 4-4z" />
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* User Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-purple-100">
              <Mail className="h-6 w-6 text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-600">Email</p>
              <p className="text-base font-semibold text-gray-900 truncate">{user.email || 'Não informado'}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-orange-100">
              <ShoppingCart className="h-6 w-6 text-orange-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Listas de Compras</p>
              <p className="text-base font-semibold text-gray-900">{shoppingLists.length} listas</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-green-100">
              <MessageSquare className="h-6 w-6 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Mensagens</p>
              <p className="text-base font-semibold text-gray-900">{messages.length} mensagens</p>
            </div>
          </div>
        </div>
      </div>

      {/* User Details */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações Adicionais</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <div className="p-2 rounded-full bg-blue-100">
              <Calendar className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Data de Cadastro</p>
              <p className="text-sm font-semibold text-gray-900">{formatDate(user.created_at)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <div className="p-2 rounded-full bg-green-100">
              <Clock className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Última Atividade</p>
              <p className="text-sm font-semibold text-gray-900">
                {user.last_activity ? formatDate(user.last_activity) : 'Nunca'}
              </p>
            </div>
          </div>
          
          {/* Categoria */}
          {user.categoria && (
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <div className={`p-2 rounded-full ${
                user.categoria === 'venda' ? 'bg-green-100' : 'bg-orange-100'
              }`}>
                <span className="text-xl">
                  {user.categoria === 'venda' ? '💰' : '🔄'}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Categoria</p>
                <p className={`text-sm font-semibold ${
                  user.categoria === 'venda' ? 'text-green-700' : 'text-orange-700'
                }`}>
                  {user.categoria === 'venda' ? 'Venda' : 'Redirecionamento'}
                </p>
              </div>
            </div>
          )}
          
          {/* Origem */}
          {user.origem && (
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <div className="p-2 rounded-full bg-purple-100">
                <span className="text-xl">
                  {user.origem === 'whatsapp' ? '💬' : user.origem === 'instagram' ? '📸' : '🔄'}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Origem</p>
                <p className="text-sm font-semibold text-gray-900 capitalize">
                  {user.origem === 'intermediary' ? 'Intermediário' : user.origem}
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Descrição Inicial */}
        {user.descricao_inicial && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-medium text-blue-900 mb-2">📋 Descrição Inicial</p>
            <p className="text-sm text-blue-800">{user.descricao_inicial}</p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('messages')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'messages'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Mensagens ({messages.length})
            </button>
            <button
              onClick={() => setActiveTab('lists')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'lists'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Listas de Compras ({shoppingLists.length})
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'messages' && (
            <div className="space-y-4">
              {/* Bot Status Info */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 mb-6 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {user.bot_enabled ? (
                      <Play className="h-5 w-5 text-green-600" />
                    ) : (
                      <Pause className="h-5 w-5 text-gray-600" />
                    )}
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        Bot {user.bot_enabled ? 'Ativo' : 'Pausado'}
                      </p>
                      <p className="text-xs text-gray-600">
                        {user.bot_enabled 
                          ? 'Respondendo automaticamente' 
                          : 'Apenas atendimento humano'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages */}
              {messages.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma mensagem</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Este usuário ainda não enviou mensagens
                  </p>
                </div>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto" ref={messagesContainerRef}>
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.direction === 'incoming' ? 'justify-start' : 'justify-end'
                        }`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${message.direction === 'incoming'
                            ? 'bg-gray-100 text-gray-900'
                            : 'bg-blue-600 text-white'
                          }`}
                      >
                        {message.content.startsWith('/uploads/') ? (() => {
                          // Separa arquivo e caption se houver ';'
                          const [filePath, caption] = message.content.split(';', 2);
                          // Detectar tipo de arquivo pela extensão
                          const isAudio = /\.(ogg|mp3|wav|m4a|aac)$/i.test(filePath);
                          const isImage = /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(filePath);
                          
                          if (isAudio) {
                            // Renderizar áudio
                            return (
                              <div>
                                <audio
                                  controls
                                  className="w-full max-w-xs"
                                  style={{ maxWidth: '300px' }}
                                >
                                  <source src={`https://crm.brazilianashop.com.br${filePath}`} />
                                  Seu navegador não suporta reprodução de áudio.
                                </audio>
                                {caption && (
                                  <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-700 whitespace-pre-wrap border-l-2 border-blue-400" style={{ fontSize: '14px' }}>
                                    <span className="font-semibold text-gray-600">📝 Transcrição:</span>
                                    <div className="mt-1">{caption}</div>
                                  </div>
                                )}
                              </div>
                            );
                          } else if (isImage) {
                            // Renderizar imagem
                            return (
                              <div>
                                <a
                                  href={`https://crm.brazilianashop.com.br${filePath}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <img
                                    src={`https://crm.brazilianashop.com.br${filePath}`}
                                    alt="Imagem enviada"
                                    className="max-w-xs lg:max-w-md rounded"
                                    style={{ maxHeight: '200px' }}
                                  />
                                </a>
                                {caption && (
                                  <div className="mt-1 text-xs text-gray-700 whitespace-pre-wrap" style={{ fontSize: '16px' }}>{caption}</div>
                                )}
                              </div>
                            );
                          } else {
                            // Arquivo desconhecido - mostrar como link
                            return (
                              <div>
                                <a
                                  href={`https://crm.brazilianashop.com.br${filePath}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-300 underline break-all"
                                >
                                  📎 {filePath.split('/').pop()}
                                </a>
                                {caption && (
                                  <div className="mt-1 text-xs text-gray-700 whitespace-pre-wrap" style={{ fontSize: '16px' }}>{caption}</div>
                                )}
                              </div>
                            );
                          }
                        })() : (
                          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        )}
                        <p
                          className={`text-xs mt-1 ${message.direction === 'incoming'
                              ? 'text-gray-500'
                              : 'text-blue-100'
                            }`}
                        >
                          {formatDate(message.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}

              {/* Indicador de envio */}
              {sendingMessage && (
                <div className="flex items-center text-blue-600 text-xs mb-2">
                  <svg className="animate-spin mr-2 h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                  </svg>
                  Enviando...
                </div>
              )}
              {/* AI Validation Result */}
              {aiValidationResult?.show && (
                <div className={`mb-4 p-4 rounded-lg border-2 ${
                  aiValidationResult.approved 
                    ? 'bg-green-50 border-green-200 text-green-800' 
                    : 'bg-red-50 border-red-200 text-red-800'
                }`}>
                  <div className="flex items-start gap-2">
                    {aiValidationResult.approved ? (
                      <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className="text-sm whitespace-pre-wrap font-medium">
                        {aiValidationResult.message}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Message Input */}
              <div className="mt-4">
                <div className="flex items-end gap-2">
                  <textarea
                    ref={textareaRef}
                    value={newMessage}
                    onChange={(e) => {
                      setNewMessage(e.target.value);
                      // Auto-resize
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                    placeholder="Digite sua mensagem..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none min-h-[42px] max-h-[200px]"
                    rows={1}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && sendOnEnter) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || aiValidating}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 h-[42px] flex items-center justify-center"
                  >
                    {aiValidating ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <Send className="h-5 w-5" />
                    )}
                  </button>
                </div>

                {/* Opções */}
                <div className="mt-2 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center text-xs text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sendOnEnter}
                        onChange={(e) => setSendOnEnter(e.target.checked)}
                        className="mr-2"
                      />
                      Enviar com Enter
                    </label>
                    {isTrainee && (
                      <div className="flex items-center text-xs text-orange-600">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Modo Trainee - IA validará suas respostas
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {sendOnEnter ? 'Shift+Enter para quebra de linha' : 'Enter para quebra de linha'}
                  </p>
                </div>
              </div>
              {!user.bot_enabled && (
                <div className="mt-2 text-xs text-red-600 flex items-center">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Bot está pausado. As mensagens serão enviadas diretamente sem processamento da IA.
                </div>
              )}
            </div>
          )}

          {activeTab === 'lists' && (
            <div className="space-y-6">
              {shoppingLists.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma lista</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Este usuário ainda não criou listas de compras
                  </p>
                </div>
              ) : (
                shoppingLists.map((list) => {
                  const appliedSubscription = getAppliedSubscription(list);

                  return (
                    <div key={list.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* List Header */}
                      <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-200">
                        <div className="flex items-center">
                          <button
                            onClick={() => toggleListExpansion(list.id)}
                            className="mr-2 p-1 rounded-full hover:bg-gray-200"
                          >
                            {expandedLists.includes(list.id) ? (
                              <ChevronUp className="h-5 w-5 text-gray-600" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-600" />
                            )}
                          </button>
                          <div>
                            <h4 className="text-lg font-medium text-gray-900">{list.name}</h4>
                            <div className="flex items-center text-sm text-gray-500">
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${list.status === 'active' ? 'bg-green-100 text-green-800' :
                                  list.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                    'bg-gray-100 text-gray-800'
                                }`}>
                                {list.status === 'active' ? 'Ativa' :
                                  list.status === 'completed' ? 'Concluída' : 'Abandonada'}
                              </span>
                              <span className="ml-2">
                                Criada em {formatDate(list.created_at)}
                              </span>
                              {list.woocommerce_order_id && (
                                <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                  Pedido #{list.woocommerce_order_id}
                                </span>
                              )}
                              {appliedSubscription && (
                                <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 flex items-center">
                                  <Package className="h-3 w-3 mr-1" />
                                  {appliedSubscription.name}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">Total</p>
                          <p className="text-lg font-bold text-gray-900">
                            {formatCurrency(calculateListTotal(list.shopping_list_items || []))}
                          </p>
                        </div>
                      </div>

                      {/* List Actions */}
                      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                        <div className="flex items-center space-x-2">
                          {/* {list.status !== 'completed' && (
                            <button
                              onClick={() => completeOrder(list.id)}
                              className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                            >
                              Completar e Criar Pedido
                            </button>
                          )} */}

                          {/* Payment Link Button */}
                          <div className="relative">
                            {showCustomAmount === list.id ? (
                              <div className="flex items-center space-x-2">
                                <input
                                  type="number"
                                  value={customAmount || ''}
                                  onChange={(e) => setCustomAmount(parseFloat(e.target.value) || 0)}
                                  placeholder="Valor em BRL"
                                  className="w-32 px-2 py-1 text-sm border border-gray-300 rounded-lg"
                                />
                                <button
                                  onClick={() => createPaymentLink(list.id)}
                                  disabled={creatingPaymentLink === list.id}
                                  className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
                                >
                                  {creatingPaymentLink === list.id ? 'Criando...' : 'Confirmar'}
                                </button>
                                <button
                                  onClick={() => setShowCustomAmount(null)}
                                  className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300"
                                >
                                  Cancelar
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => openPaymentLinkForm(list.id, list.shopping_list_items || [])}
                                className="flex items-center px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                              >
                                <DollarSign className="h-4 w-4 mr-1" />
                                Link de Pagamento
                              </button>
                            )}
                          </div>

                          {list.woocommerce_order_id && (
                            <a
                              href={`#`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center px-3 py-1 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
                            >
                              <ExternalLink className="h-4 w-4 mr-1" />
                              Ver no WooCommerce
                            </a>
                          )}
                        </div>

                        {/* Subscription Actions */}
                        <div className="flex items-center space-x-2">
                          {/* Apply Subscription Dropdown */}
                          {/* {subscriptions.length > 0 && !appliedSubscription && (
                            <div className="flex items-center space-x-2">
                              <select
                                value={selectedSubscription}
                                onChange={(e) => setSelectedSubscription(e.target.value)}
                                className="text-sm border border-gray-300 rounded-lg px-2 py-1"
                              >
                                <option value="">Aplicar Assinatura</option>
                                {subscriptions.map((subscription) => {
                                  const canApply = canApplySubscription(list, subscription);
                                  return (
                                    <option
                                      key={subscription.id}
                                      value={subscription.id}
                                      disabled={!canApply}
                                    >
                                      {subscription.name} - ${subscription.price_monthly}/mês
                                      {!canApply && ' (Peso excede limite)'}
                                    </option>
                                  );
                                })}
                              </select>

                              {selectedSubscription && (
                                <button
                                  onClick={() => applySubscription(list.id, selectedSubscription)}
                                  disabled={applyingSubscription === list.id}
                                  className="px-3 py-1 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50"
                                >
                                  {applyingSubscription === list.id ? 'Aplicando...' : 'Aplicar'}
                                </button>
                              )}
                            </div>
                          )} */}

                          {/* Remove Subscription Button */}
                          {/* {appliedSubscription && (
                            <button
                              onClick={() => removeSubscription(list.id)}
                              disabled={removingSubscription === list.id}
                              className="flex items-center px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
                              title="Remover assinatura"
                            >
                              <X className="h-4 w-4 mr-1" />
                              {removingSubscription === list.id ? 'Removendo...' : 'Remover Assinatura'}
                            </button>
                          )} */}
                        </div>
                      </div>

                      {/* List Content */}
                      {expandedLists.includes(list.id) && (
                        <div className="p-4">
                          {list.shopping_list_items && list.shopping_list_items.length > 0 ? (
                            <div className="space-y-4">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produto</th>
                                    <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Qtd</th>
                                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Preço</th>
                                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {list.shopping_list_items.map((item) => (
                                    <tr key={item.id}>
                                      <td className="px-3 py-2 whitespace-nowrap">
                                        <div className="flex items-center">
                                          {item.product_image && (
                                            <img
                                              src={item.product_image}
                                              alt={item.product_name}
                                              className="h-10 w-10 rounded-md object-cover mr-3"
                                            />
                                          )}
                                          <div className="text-sm font-medium text-gray-900">
                                            {item.product_name}
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 text-center">
                                        {item.quantity}
                                      </td>
                                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                                        {formatCurrency(item.product_price)}
                                      </td>
                                      <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                                        {formatCurrency(item.product_price * item.quantity)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>

                              {/* Pricing Summary */}
                              <div className="mt-6 border-t border-gray-200 pt-4">
                                <h5 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                                  <DollarSign className="h-4 w-4 mr-1 text-green-600" />
                                  Resumo do Orçamento
                                  {appliedSubscription && (
                                    <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                                      Com {appliedSubscription.name}
                                    </span>
                                  )}
                                </h5>

                                <div className="bg-gray-50 rounded-lg p-4">
                                  <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-600">Subtotal:</span>
                                      <span className="font-medium">{formatCurrency(calculateListTotal(list.shopping_list_items))}</span>
                                    </div>

                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-600">Taxa de Serviço (3.5%):</span>
                                      <span>{formatCurrency(calculateServiceFee(list.shopping_list_items))}</span>
                                    </div>

                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-600">
                                        Impostos (8.5%):
                                        {!applyTaxesOnPayment && (
                                          <span className="text-orange-600 ml-1">(apenas informativo)</span>
                                        )}
                                      </span>
                                      <span className={!applyTaxesOnPayment ? "text-orange-600" : ""}>
                                        {formatCurrency(calculateTaxes(list.shopping_list_items))}
                                      </span>
                                    </div>

                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-600">
                                        Frete:
                                        {appliedSubscription && (
                                          <span className="text-blue-600 ml-1">(taxa fixa)</span>
                                        )}
                                      </span>
                                      <span>{formatCurrency(calculateShippingFee(list.shopping_list_items, appliedSubscription))}</span>
                                    </div>

                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-600">
                                        Taxa de Handling:
                                        {appliedSubscription && (
                                          <span className="text-blue-600 ml-1">(taxa fixa)</span>
                                        )}
                                      </span>
                                      <span>{formatCurrency(calculateHandlingFee(list.shopping_list_items, appliedSubscription))}</span>
                                    </div>

                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-600">
                                        Impostos Brasil (80% do produto + frete + handling):
                                        {!applyTaxesOnPayment && (
                                          <span className="text-orange-600 ml-1">(apenas informativo)</span>
                                        )}
                                      </span>
                                      <span className={!applyTaxesOnPayment ? "text-orange-600" : ""}>
                                        {formatCurrency(calculateBrazilianTaxes(
                                          list.shopping_list_items,
                                          calculateShippingFee(list.shopping_list_items, appliedSubscription),
                                          calculateHandlingFee(list.shopping_list_items, appliedSubscription)
                                        ))}
                                      </span>
                                    </div>

                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-600 flex items-center">
                                        <Weight className="h-4 w-4 mr-1" />
                                        Peso Total:
                                      </span>
                                      <span>{calculateTotalWeight(list.shopping_list_items).toFixed(2)}kg</span>
                                    </div>

                                    <div className="flex justify-between font-bold text-green-700 mt-2">
                                      <span>Total em BRL (5.9x):</span>
                                      <span>
                                        {new Intl.NumberFormat('pt-BR', {
                                          style: 'currency',
                                          currency: 'BRL'
                                        }).format(calculateTotalBRL(calculateTotalUSD(list.shopping_list_items, appliedSubscription)))}
                                      </span>
                                    </div>

                                    {appliedSubscription && (
                                      <div className="border-t border-blue-200 pt-2 mt-2 bg-blue-50 rounded p-2">
                                        <div className="text-sm text-blue-700">
                                          <div className="font-medium mb-1">Benefícios da Assinatura {appliedSubscription.name}:</div>
                                          <ul className="text-xs space-y-1">
                                            {appliedSubscription.benefits.map((benefit, index) => (
                                              <li key={index} className="flex items-start">
                                                <CheckCircle className="h-3 w-3 text-blue-600 mr-1 mt-0.5 flex-shrink-0" />
                                                {benefit}
                                              </li>
                                            ))}
                                          </ul>
                                          {appliedSubscription.limitations.length > 0 && (
                                            <div className="mt-2">
                                              <div className="font-medium mb-1">Limitações:</div>
                                              <ul className="text-xs space-y-1">
                                                {appliedSubscription.limitations.map((limitation, index) => (
                                                  <li key={index} className="flex items-start">
                                                    <AlertTriangle className="h-3 w-3 text-orange-600 mr-1 mt-0.5 flex-shrink-0" />
                                                    {limitation}
                                                  </li>
                                                ))}
                                              </ul>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 italic">Lista vazia</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsuarioDetalhe;